import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { Category, type Listing } from '@smg-real-estate/hg-rets-schema';
import type { ListingItem, SearchModel } from '@smg-real-estate/hg-search-libs';
import {
  getHgCoreLinkAttributes,
  getHgCoreMetaProperties,
  getIs24CoreLinkAttributes,
  getIs24CoreMetaProperties,
} from '@smg-real-estate/metatags-seo';
import { type SafeLink, type SafeMeta, type SafeScript } from '@unhead/schema';
import { UseHeadInput } from '@unhead/vue';

import type { BreadCrumbModel } from '@/components/BreadCrumbs/BreadCrumbModel';
import {
  getListingCurrency,
  getListingDisplayPrice,
  getListingTitle,
  getTransformedListingImageUrl,
} from '@/helpers/ListingData/ListingData';
import { localizeLanguage } from '@/helpers/LocalizeLanguage/LocalizeLanguage';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { cleanupMarkupTags, truncateText } from '@/helpers/TextFormatting/TextFormatting';
import { ComposerTranslation } from 'vue-i18n';

export const getAppMetaInfo = (title: string, locale: HgSupportedLanguage): UseHeadInput => {
  return {
    title: title,
    htmlAttrs: {
      lang: localizeLanguage(locale),
    },
    meta: (isPlatformIs24()
      ? getIs24CoreMetaProperties()
      : getHgCoreMetaProperties()) as SafeMeta[],
    link: (isPlatformIs24()
      ? getIs24CoreLinkAttributes()
      : getHgCoreLinkAttributes()) as SafeLink[],
  };
};

const LD_JSON_TYPE = 'application/ld+json';
const LD_JSON_CONTEXT = 'http://schema.org';
const LD_JSON_TYPE_PRODUCT = 'Product';

const hasSearchFilters = (searchModel: SearchModel): boolean => {
  // Check if any range filters have values
  const hasRangeFilters = [
    searchModel.numberOfRooms,
    searchModel.priceRange,
    searchModel.surfaceLivingRange,
    searchModel.buildYearRange,
    searchModel.surfacePropertyRange,
    searchModel.surfaceUsableRange,
    searchModel.volumeRange,
    searchModel.yearlyRentPerSqmRange,
    searchModel.numberOfApartments,
  ].some((range) => range?.from !== undefined || range?.to !== undefined);

  // Check if any array filters have values
  const hasArrayFilters = [searchModel.objectTypes, searchModel.facilitiesRequired].some(
    (array) => array && array.length > 0,
  );

  // Check if any other filters have values
  const hasOtherFilters = [searchModel.floorType, searchModel.availability, searchModel.text].some(
    (value) => value !== undefined,
  );

  return hasRangeFilters || hasArrayFilters || hasOtherFilters;
};

export const getLdJsonByBreadcrumbs = (breadCrumbs: BreadCrumbModel[]): SafeScript[] => {
  return [
    {
      type: LD_JSON_TYPE,
      'data-vmid': 'ld-json-breadcrumbs',
      innerHTML: {
        '@context': LD_JSON_CONTEXT,
        '@type': 'BreadcrumbList',
        itemListElement: (breadCrumbs || []).map(({ name, link }, index) => {
          return {
            '@type': 'ListItem',
            position: index + 1,
            name: String(name),
            item: link,
          };
        }),
      },
    },
  ];
};

export const getLdJsonByListing = (
  listing: Listing | undefined,
  {
    listingUrl,
    listingCategory,
    listingImage,
    listingTitle,
    listingDescription,
  }: {
    listingUrl?: string;
    listingCategory?: string;
    listingImage?: string;
    listingTitle?: string;
    listingDescription?: string;
  } = {},
): SafeScript[] => {
  if (!listing) {
    return [];
  }

  const scripts: SafeScript[] = [
    {
      type: LD_JSON_TYPE,
      'data-vmid': 'ld-json-listing-date',
      innerHTML: {
        '@context': LD_JSON_CONTEXT,
        '@type': 'RealEstateListing',
        datePosted: listing.meta?.createdAt ?? '',
      },
    },
  ];

  if (
    listingImage &&
    listing?.prices &&
    getListingDisplayPrice(listing) &&
    getListingCurrency(listing)
  ) {
    scripts.push({
      type: LD_JSON_TYPE,
      'data-vmid': 'ld-json-listing',
      innerHTML: {
        '@context': LD_JSON_CONTEXT,
        '@type': LD_JSON_TYPE_PRODUCT,
        name: truncateText(listingTitle || '', 64),
        image: String(listingImage),
        description: truncateText(cleanupMarkupTags(listingDescription || ''), 355),
        url: String(listingUrl),
        offers: [
          {
            '@context': LD_JSON_CONTEXT,
            '@type': 'Offer',
            category: String(listingCategory),
            price: String(getListingDisplayPrice(listing)),
            priceCurrency: String(getListingCurrency(listing)),
            url: String(listingUrl),
          },
        ],
      },
    });
  }
  return scripts;
};

export const getLdJsonBySearchResults = ({
  listings,
  t,
  pageTitle,
  pageDescription,
  resultCount,
  isMainPage,
  page,
  searchModel,
}: {
  listings: ListingItem[] | undefined;
  t: ComposerTranslation;
  pageTitle: string;
  pageDescription: string;
  resultCount: number;
  isMainPage: boolean;
  page: number;
  searchModel?: SearchModel;
}): SafeScript[] => {
  if (!listings || page > 5) {
    return [];
  }

  const ldJsonMarkup: SafeScript[] = [];
  const prices = listings
    .map((item) => {
      return getListingDisplayPrice(item.listing) || 0;
    })
    .filter((price: number) => price > 0);

  // Add AggregateOffer only on main SRP page and if there are listings
  if (isMainPage && listings.length > 0) {
    ldJsonMarkup.push({
      type: LD_JSON_TYPE,
      'data-vmid': 'ld-json-aggregate',
      textContent: {
        '@context': LD_JSON_CONTEXT,
        '@type': LD_JSON_TYPE_PRODUCT,
        name: pageTitle || '',
        description: pageDescription || '',
        offers: {
          '@type': 'AggregateOffer',
          offerCount: resultCount,
          lowPrice: prices.length ? Math.min(...prices) : 0,
          highPrice: prices.length ? Math.max(...prices) : 0,
          priceCurrency: 'CHF',
        },
      },
    });
  }

  // Add individual Product markup for each listing only if no filters are applied
  if (!searchModel || !hasSearchFilters(searchModel)) {
    listings.forEach(({ listing }) => {
      const markup = getLdJsonListing(
        listing,
        getTransformedListingImageUrl(listing) || '',
        getListingTitle(listing, t) || '',
      );

      if (markup) {
        ldJsonMarkup.push(markup);
      }
    });
  }

  return ldJsonMarkup;
};

const getLdJsonListing = (
  listing: Listing,
  listingImage: string,
  listingTitle: string,
): SafeScript | null => {
  const listingPrice = getListingDisplayPrice(listing);

  if (!listingImage || !listing?.prices || !listingImage) {
    return null;
  }

  return {
    type: LD_JSON_TYPE,
    'data-vmid': 'ld-json-listing',
    textContent: {
      '@context': LD_JSON_CONTEXT,
      '@type': LD_JSON_TYPE_PRODUCT,
      name: listingTitle,
      image: String(listingImage),
      offers: [
        {
          '@context': LD_JSON_CONTEXT,
          '@type': 'Offer',
          price: listingPrice || 0,
          priceCurrency: getListingCurrency(listing) || 'CHF',
        },
      ],
    },
  };
};

// The listing is eligible for enhanced SEO title generation for apartments, houses, and parking spaces property types
// House and apartment
export const HOUSE_AND_APARTMENT_CATEGORIES = [
  Category.Apartment,
  Category.Maisonette,
  Category.Duplex,
  Category.AtticFlat,
  Category.RoofFlat,
  Category.Studio,
  Category.SingleRoom,
  Category.TerraceFlat,
  Category.BachelorFlat,
  Category.FurnishedFlat,
  Category.Loft,
  Category.Chalet,
  Category.Attic,
  Category.GrannyFlat,
  Category.GardenApartment,
  Category.SharedApartment,
  Category.House,
  Category.SingleHouse,
  Category.TerraceHouse,
  Category.HousePart,
  Category.BifamiliarHouse,
  Category.CaveHouse,
  Category.EngadineHouse,
  Category.PatricianHouse,
  Category.RowHouse,
  Category.RusticHouse,
  Category.Villa,
  Category.FarmHouse,
];

// Parking space
export const PARKING_SPACE_CATEGORIES = [
  Category.Garage,
  Category.SingleGarage,
  Category.DoubleGarage,
  Category.CoveredSlot,
  Category.OpenSlot,
  Category.UndergroundSlot,
  Category.ParkingSpace,
  Category.CarPark,
  Category.OutdoorParkingPlaceBike,
  Category.CoveredParkingPlaceBike,
];
export const ALLOWED_SEO_ENHANCEMENT_CATEGORIES = new Set([
  ...HOUSE_AND_APARTMENT_CATEGORIES,
  ...PARKING_SPACE_CATEGORIES,
]);

/**
 * The listing is eligible for enhanced SEO title generation on IS24 and for
 * apartments, houses, and parking spaces property types.
 * @param listing - The listing object to validate
 * @returns boolean - True if listing qualifies for enhanced SEO title template
 */
export function isListingEligibleForEnhancedSeoTitle(listing: Listing): boolean {
  return Boolean(
    isPlatformIs24() &&
    listing.categories?.some((category) => ALLOWED_SEO_ENHANCEMENT_CATEGORIES.has(category)),
  );
}
